import { useState, useEffect } from 'react';
import { X, Save, AlertTriangle } from 'lucide-react';
import { newReleasesApi } from '../../services/adminApi';
import BookSearchSelect from './BookSearchSelect';

const NewReleasesItemModal = ({ isOpen, onClose, item = null, listContext, onSuccess }) => {
  const [formData, setFormData] = useState({
    book_id: '',
    rank: 1,
    note: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      if (item) {
        setFormData({
          book_id: item.book_id || '',
          rank: item.rank || 1,
          note: item.note || ''
        });
      } else {
        setFormData({
          book_id: '',
          rank: 1,
          note: ''
        });
      }
      setError(null);
    }
  }, [isOpen, item]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.book_id) {
      setError('Please search for and select a book.');
      return;
    }

    if (!formData.rank || formData.rank < 1) {
      setError('Rank must be a positive number.');
      return;
    }

    if (!listContext?.month) {
      setError('List context (month) is required.');
      return;
    }

    try {
      setLoading(true);
      const apiData = {
        book_id: parseInt(formData.book_id),
        month: listContext.month,
        rank: parseInt(formData.rank),
        note: formData.note.trim() || null
      };

      if (listContext.age_group) {
        apiData.age_group = listContext.age_group;
      }

      if (item) {
        await newReleasesApi.updateNewReleasesItem(item.id, {
          rank: apiData.rank,
          note: apiData.note
        });
      } else {
        await newReleasesApi.addNewReleasesItem(apiData);
      }

      onSuccess();
      onClose();
    } catch (err) {
      let errorMessage = err.message || 'Failed to save item';
      if (errorMessage.includes('Book not found')) {
        errorMessage = `Book ID ${formData.book_id} not found. Please verify the book ID exists.`;
      } else if (errorMessage.includes('already exists')) {
        errorMessage = 'This book already exists in this New Releases list.';
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-text-primary">
            {item ? 'Edit New Releases Item' : 'Add Item to New Releases'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <span className="text-red-800">{error}</span>
              </div>
            </div>
          )}

          {listContext && (
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-sm text-text-secondary">
                Adding to:
                <span className="font-medium"> {listContext.month}</span>
                {listContext.age_group ? (
                  <>
                    {' '}<span className="text-text-secondary">for ages</span>
                    <span className="font-medium"> {listContext.age_group}</span>
                  </>
                ) : (
                  <>
                    {' '}<span className="text-text-secondary">(book suitability comes from canonical reading age)</span>
                  </>
                )}
              </p>
            </div>
          )}

          <BookSearchSelect
            label="Book"
            required
            disabled={!!item}
            selectedId={formData.book_id}
            selectedLabel={item?.title || ''}
            onSelect={(book) => handleInputChange('book_id', book ? book.id : '')}
            helperText={item ? 'The book cannot be changed on an existing item.' : 'Search by title or author.'}
          />

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">Rank *</label>
            <input
              type="number"
              min="1"
              value={formData.rank}
              onChange={(e) => handleInputChange('rank', e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-200"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">Note (Optional)</label>
            <textarea
              value={formData.note}
              onChange={(e) => handleInputChange('note', e.target.value)}
              placeholder="Add a note about this book selection..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-6 py-2 border border-gray-200 text-text-secondary rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary flex items-center space-x-2 disabled:opacity-50"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              <span>{loading ? 'Saving...' : (item ? 'Update Item' : 'Add Item')}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewReleasesItemModal;
